var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import { Link } from 'react-router-dom';
import Button from '../../../Elements/Button/Button';
import Checkbox from '../../../Forms/Checkbox';
import { rowClassSymbol } from '../types';
import { getActionCellSizeProps } from '../utils';
import EditIcon from './icons/Edit';
import EyeOpenIcon from './icons/EyeOpen';
import { x } from '@xstyled/styled-components';
import { isFunction } from 'lodash/fp';
import MemoDataCell from './DataCell';
import { Cell, StyledDataRow } from './Table';
import { useTimeMeasurement } from './utils';
var EmptySpan = function (_a) {
    var children = _a.children, props = __rest(_a, ["children"]);
    return _jsx("span", __assign({}, props, { children: children }));
};
var DataRow = function (_a) {
    var _b, _c, _d;
    var columns = _a.columns, actionColumn = _a.actionColumn, rowData = _a.rowData, rowIndex = _a.rowIndex, uxState = _a.uxState, allowRowSelect = _a.allowRowSelect, allowRowSelectOnAction = _a.allowRowSelectOnAction, onUxChange = _a.onUxChange, onRowEditClick = _a.onRowEditClick, onRowReadClick = _a.onRowReadClick, onRowClick = _a.onRowClick, onRowAction = _a.onRowAction, displayColumnsWidth = _a.displayColumnsWidth, groups = _a.groups, isEven = _a.isEven;
    var isChecked = !!((_b = uxState.checkedRows) === null || _b === void 0 ? void 0 : _b[rowData.id]);
    var onCheckboxChange = React.useCallback(function () {
        var checkedRows = __assign({}, (uxState.checkedRows || {}));
        if (!isChecked) {
            checkedRows[rowData.id] = true;
        }
        else {
            delete checkedRows[rowData.id];
        }
        onUxChange && onUxChange('checkedRows', checkedRows);
    }, [onUxChange, isChecked, uxState, rowData]);
    var handleEditClick = React.useCallback(function () {
        if (onRowEditClick)
            onRowEditClick(rowData.id, rowData);
        if (onUxChange && allowRowSelectOnAction)
            onUxChange('selectedRow', rowData.id);
    }, [onRowEditClick, rowData]);
    var handleReadClick = React.useCallback(function () {
        if (onRowReadClick)
            onRowReadClick(rowData.id, rowData);
        if (onUxChange && allowRowSelectOnAction)
            onUxChange('selectedRow', rowData.id);
    }, [onRowReadClick, rowData]);
    var handleRowAction = React.useCallback(function (id, field, value, rowData) {
        if (onRowAction)
            onRowAction(id, field, value, rowData);
        if (onUxChange && allowRowSelectOnAction)
            onUxChange('selectedRow', id);
    }, [onRowReadClick, rowData]);
    var handleRowClick = React.useCallback(function (e) {
        // test whether I should trigger event or not (because of possible button/link/input/etc in cell)
        var classes = Object.values(e.target.classList);
        if (!classes.includes('dataRow') && !classes.includes('cell'))
            return;
        if (onUxChange && allowRowSelect)
            onUxChange('selectedRow', rowData.id);
        if (onRowClick)
            onRowClick(rowData.id, rowData);
    }, 
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onRowClick, onUxChange, rowData]);
    var _e = useTimeMeasurement({ handleRowClick: handleRowClick }), handleMouseDown = _e.handleMouseDown, handleMouseUp = _e.handleMouseUp;
    var RowActionComponent = ((actionColumn === null || actionColumn === void 0 ? void 0 : actionColumn.createLink) ? Link : EmptySpan);
    var rowActionProps = React.useCallback(function (mode) {
        var _a;
        return (actionColumn === null || actionColumn === void 0 ? void 0 : actionColumn.createLink)
            ? { to: (_a = actionColumn === null || actionColumn === void 0 ? void 0 : actionColumn.createLink) === null || _a === void 0 ? void 0 : _a.call(actionColumn, { mode: mode, id: rowData.id }) }
            : { onClick: { read: handleReadClick, edit: handleEditClick }[mode] };
    }, [actionColumn, handleReadClick, handleEditClick]);
    var rowClassName = React.useMemo(function () {
        var classes = ['dataRow'];
        if (isChecked)
            classes.push('checked');
        if (uxState.selectedRow == rowData.id && (allowRowSelect || allowRowSelectOnAction))
            classes.push('selected');
        if (rowData[rowClassSymbol])
            classes.push(rowData[rowClassSymbol] || '');
        return classes.join(' ');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isChecked, uxState, rowData]);
    var actionColumnStyle = React.useMemo(function () {
        return actionColumn ? __assign({}, getActionCellSizeProps(actionColumn)) : {};
    }, [actionColumn]);
    var displayReadEditCell = ((_c = actionColumn === null || actionColumn === void 0 ? void 0 : actionColumn.actionOptions) === null || _c === void 0 ? void 0 : _c.length) || (actionColumn === null || actionColumn === void 0 ? void 0 : actionColumn.canRowEdit) || (actionColumn === null || actionColumn === void 0 ? void 0 : actionColumn.canRowRead) || (actionColumn === null || actionColumn === void 0 ? void 0 : actionColumn.forceCheckboxes);
    return (_jsxs(StyledDataRow, { className: rowClassName, hasRowClick: !!onRowClick, onMouseDown: handleMouseDown, onMouseUp: handleMouseUp, isEven: !!isEven, children: [actionColumn && (_jsxs(_Fragment, { children: [displayReadEditCell && (_jsxs(Cell, { className: "cell", style: actionColumnStyle, children: [((_d = actionColumn === null || actionColumn === void 0 ? void 0 : actionColumn.actionOptions) === null || _d === void 0 ? void 0 : _d.length) || (actionColumn === null || actionColumn === void 0 ? void 0 : actionColumn.forceCheckboxes) ? (_jsx(Checkbox, { checked: isChecked, onChange: onCheckboxChange, label: "" })) : null, _jsxs(x.div, { children: [(isFunction(actionColumn.canRowRead) ? actionColumn.canRowRead(rowData) : actionColumn.canRowRead) && (_jsx(RowActionComponent, __assign({}, rowActionProps('read'), { children: _jsx(Button, { type: "button", variant: "default", appearance: "primary", children: _jsx(EyeOpenIcon, { width: "16px", height: "16px" }) }) }))), (isFunction(actionColumn.canRowEdit) ? actionColumn.canRowEdit(rowData) : actionColumn.canRowEdit) && (_jsx(RowActionComponent, __assign({}, rowActionProps('edit'), { children: _jsx(Button, { type: "button", variant: "default", appearance: "primary", children: _jsx(EditIcon, { width: "16px", height: "16px" }) }) })))] })] })), (actionColumn === null || actionColumn === void 0 ? void 0 : actionColumn.addRowNumbers) && (_jsx(Cell, { className: "cell", style: actionColumnStyle, children: _jsx(x.div, { children: rowIndex }) }))] })), columns.map(function (column, key) { return (_jsx(MemoDataCell, { column: column, rowData: rowData, onRowAction: handleRowAction, displayColumnWidth: displayColumnsWidth[column.name], group: groups === null || groups === void 0 ? void 0 : groups[key] }, column.name)); })] }));
};
export default DataRow;
